import { DEFERRED_AI_ACTIONS_V1 } from "../schemas/ai-action-policy";
import type { CampaignCreationCapabilities } from "../schemas/campaign-creation";
import { canonicalSmartCreationTargetIntentKey } from "../schemas/smart-creation";
import {
  isSmartCreationNegativeKeywordWithinWordLimit,
  type SmartCreationReviewCampaignSeed,
  type SmartCreationReviewChange,
  type SmartCreationReviewNegative,
  type SmartCreationReviewTarget,
  type SmartCreationReviewWorkspace,
} from "../schemas/smart-creation-review";
import {
  applyBudgetAllocations,
  redistributeBudget,
  redistributeBudgetForCampaignInclusion,
} from "./budget";
import { addCampaignCopy, campaignCopySeed } from "./campaign-copy";
import { SmartCreationReviewError } from "./errors";
import {
  draftContentAdGroupFor,
  generatedValue,
  requireAdGroup,
  requireCampaign,
  requireNegative,
  requireTarget,
  userValue,
} from "./graph";
import { moveKeywordTargetToMatchType } from "./keyword-match-type";
import { targetStableKey } from "./lineage";
import {
  reviewTargetIntent,
  strategyOwnsAdGroup,
  strategySupportsReviewIntent,
} from "./strategy-intent";
import type { ReviewScheduleContext } from "./types";
import { round, validateNumeric } from "./validation";

export function applyChange(
  workspace: SmartCreationReviewWorkspace,
  change: SmartCreationReviewChange,
  capabilities: CampaignCreationCapabilities,
  campaignSeed?: SmartCreationReviewCampaignSeed,
  scheduleContext?: ReviewScheduleContext,
): void {
  const { graph } = workspace;
  switch (change.operation) {
    case "set_ai_action_policy": {
      const previous = workspace.aiActionPolicy;
      const policy = change.policy;
      if (
        previous?.source !== "smart_creation" ||
        policy.source !== "smart_creation" ||
        policy.shopId !== previous.shopId ||
        policy.planId !== previous.planId ||
        policy.revision !== previous.revision + 1 ||
        DEFERRED_AI_ACTIONS_V1.some((action) => policy.actions[action])
      ) {
        throw new SmartCreationReviewError(
          422,
          "smart_creation.review_ai_policy_invalid",
          "The AI action policy is stale, cross-Shop or includes an action without an executor",
        );
      }
      if (policy.bidBounds) {
        validateNumeric(
          policy.bidBounds.min,
          capabilities.bid,
          "smart_creation.review_ai_policy_invalid",
        );
        validateNumeric(
          policy.bidBounds.max,
          capabilities.bid,
          "smart_creation.review_ai_policy_invalid",
        );
      }
      workspace.aiActionPolicy = policy;
      return;
    }
    case "add_campaign":
      addCampaignCopy(
        workspace,
        change,
        capabilities,
        campaignSeed ?? campaignCopySeed(workspace, change),
      );
      return;
    case "set_campaign_budget_allocations":
      applyBudgetAllocations(graph, change, capabilities);
      return;
    case "set_plan_schedule": {
      if (change.endDate && change.endDate < change.startDate) {
        throw new SmartCreationReviewError(
          422,
          "smart_creation.review_schedule_invalid",
          "Campaign end date must not precede its start date",
        );
      }
      if (scheduleContext) {
        const today = scheduleContext.now.toISOString().slice(0, 10);
        if (change.startDate < today) {
          throw new SmartCreationReviewError(
            422,
            "smart_creation.campaign_dates_invalid",
            "Campaign start date must be today or in the future",
          );
        }
        const promotion = scheduleContext.promotionDateRange;
        if (
          promotion &&
          (promotion.startDate < change.startDate ||
            (change.endDate !== null && promotion.endDate > change.endDate) ||
            promotion.endDate < today)
        ) {
          throw new SmartCreationReviewError(
            422,
            "smart_creation.promotion_dates_invalid",
            "Promotion dates must remain within the Campaign schedule and must not have ended",
          );
        }
      }
      workspace.planSettings.startDate = change.startDate;
      workspace.planSettings.endDate = change.endDate;
      workspace.draftContent.campaigns = workspace.draftContent.campaigns.map((campaign) => ({
        ...campaign,
        startDate: change.startDate,
        endDate: change.endDate,
      }));
      return;
    }
    case "set_naming_rule":
      workspace.planSettings.namingRule = change.namingRule;
      return;
    case "set_plan_name":
      workspace.planSettings.name = userValue(workspace.planSettings.name, change.name);
      return;
    case "set_portfolio":
      workspace.planSettings.portfolioId = userValue(
        workspace.planSettings.portfolioId,
        change.portfolioId,
      );
      return;
    case "set_campaign_name": {
      if (change.name.length > capabilities.campaignName.maxLength) {
        throw new SmartCreationReviewError(
          422,
          "smart_creation.review_name_invalid",
          "Campaign name is too long",
        );
      }
      const campaign = requireCampaign(graph, change.ref);
      campaign.name = userValue(campaign.name, change.name);
      return;
    }
    case "set_campaign_budget_allocation": {
      const campaign = requireCampaign(graph, change.ref);
      if (campaign.excluded.effective) {
        throw new SmartCreationReviewError(
          422,
          "smart_creation.review_budget_invalid",
          "An excluded Campaign cannot receive a budget allocation",
        );
      }
      const planTotal = graph.campaigns.reduce(
        (total, candidate) =>
          candidate.excluded.effective ? total : total + candidate.dailyBudget.effective,
        0,
      );
      const amount =
        change.allocation.basis === "amount"
          ? change.allocation.amount
          : round(
              (planTotal * change.allocation.basisPoints) / 10_000,
              capabilities.dailyBudget.decimals,
            );
      validateNumeric(amount, capabilities.dailyBudget, "smart_creation.review_budget_invalid");
      redistributeBudget(graph, campaign, amount, capabilities);
      campaign.budgetAllocation = userValue(campaign.budgetAllocation, change.allocation);
      return;
    }
    case "set_campaign_bidding_strategy": {
      if (!capabilities.biddingStrategies.includes(change.biddingStrategy)) {
        throw new SmartCreationReviewError(
          422,
          "smart_creation.review_bidding_strategy_invalid",
          "Bidding strategy is unavailable in the current marketplace",
        );
      }
      const campaign = requireCampaign(graph, change.ref);
      campaign.biddingStrategy = userValue(campaign.biddingStrategy, change.biddingStrategy);
      return;
    }
    case "set_campaign_placement": {
      if (
        !capabilities.placementAdjustment.placements.includes(change.placement) ||
        change.percentage < capabilities.placementAdjustment.min ||
        change.percentage > capabilities.placementAdjustment.max
      ) {
        throw new SmartCreationReviewError(
          422,
          "smart_creation.review_placement_invalid",
          "Placement adjustment is unavailable in the current marketplace",
        );
      }
      const campaign = requireCampaign(graph, change.ref);
      const previous = campaign.placements.find((item) => item.placement === change.placement);
      if (previous) previous.percentage = userValue(previous.percentage, change.percentage);
      else {
        campaign.placements.push({
          placement: change.placement,
          percentage: {
            ...generatedValue(0),
            effective: change.percentage,
          },
        });
      }
      return;
    }
    case "remove_campaign_placement": {
      const campaign = requireCampaign(graph, change.ref);
      campaign.placements = campaign.placements.filter(
        (item) => item.placement !== change.placement,
      );
      return;
    }
    case "set_campaign_excluded": {
      const campaign = requireCampaign(graph, change.ref);
      redistributeBudgetForCampaignInclusion(graph, campaign, change.excluded, capabilities);
      return;
    }
    case "set_ad_group_name": {
      const adGroup = requireAdGroup(graph, change.ref);
      adGroup.name = userValue(adGroup.name, change.name);
      return;
    }
    case "set_ad_group_default_bid": {
      validateNumeric(change.defaultBid, capabilities.bid, "smart_creation.review_bid_invalid");
      const adGroup = requireAdGroup(graph, change.ref);
      adGroup.defaultBid = userValue(adGroup.defaultBid, change.defaultBid);
      return;
    }
    case "set_target_bid": {
      validateNumeric(change.bid, capabilities.bid, "smart_creation.review_bid_invalid");
      const target = requireTarget(graph, change.ref);
      target.bid = userValue(target.bid, change.bid);
      return;
    }
    case "set_target_match_type": {
      const target = requireTarget(graph, change.ref);
      if (target.type !== "keyword") {
        throw new SmartCreationReviewError(
          422,
          "smart_creation.review_target_invalid",
          "Only keyword Targets have a Match Type",
        );
      }
      moveKeywordTargetToMatchType(workspace, target, change.matchType);
      target.matchType = userValue(target.matchType, change.matchType);
      return;
    }
    case "set_target_excluded": {
      const target = requireTarget(graph, change.ref);
      target.excluded = userValue(target.excluded, change.excluded);
      if (workspace.draftContent.structure === "precise" && target.type === "automatic") {
        const group = graph.adGroups.find(
          (item) => item.ref.lineageKey === target.adGroupLineageKey,
        );
        if (!group) throw new Error("Automatic Target Ad Group is missing");
        const campaign = graph.campaigns.find(
          (item) => item.ref.lineageKey === group.campaignLineageKey,
        );
        if (!campaign) throw new Error("Automatic Target Campaign is missing");
        const excluded = graph.targets
          .filter((item) => campaign.adGroupLineageKeys.includes(item.adGroupLineageKey))
          .every((item) => item.excluded.effective);
        // In Precise, selecting an expression selects its singleton Campaign.
        redistributeBudgetForCampaignInclusion(graph, campaign, excluded, capabilities);
      }
      return;
    }
    case "add_target": {
      const adGroup = requireAdGroup(graph, change.adGroupRef);
      const draftContentAdGroup = draftContentAdGroupFor(
        workspace.draftContent,
        adGroup.ref.itemKey,
      );
      const targetMatchesLane =
        (draftContentAdGroup.targeting.type === "keyword" &&
          change.target.type === "keyword" &&
          draftContentAdGroup.targeting.matchType === change.target.matchType) ||
        (draftContentAdGroup.targeting.type === "product" && change.target.type === "product");
      if (
        !targetMatchesLane ||
        !strategySupportsReviewIntent(
          change.target.strategyKind,
          reviewTargetIntent(change.target),
          workspace.draftContent.previewGeneratorVersion,
        ) ||
        !strategyOwnsAdGroup(
          draftContentAdGroup,
          change.target.strategyKind,
          workspace.draftContent.previewGeneratorVersion,
        )
      ) {
        throw new SmartCreationReviewError(
          422,
          "smart_creation.review_target_invalid",
          "New Target type and Match Type must match the selected Ad Group lane",
        );
      }
      if (
        graph.targets.some(
          (target) =>
            target.ref.lineageKey === change.target.ref.lineageKey ||
            (target.adGroupLineageKey === adGroup.ref.lineageKey &&
              canonicalSmartCreationTargetIntentKey(reviewTargetIntent(target)) ===
                canonicalSmartCreationTargetIntentKey(reviewTargetIntent(change.target))),
        )
      ) {
        throw new SmartCreationReviewError(
          422,
          "smart_creation.review_target_invalid",
          "Target already exists",
        );
      }
      validateNumeric(change.target.bid, capabilities.bid, "smart_creation.review_bid_invalid");
      const itemKey = targetStableKey({
        adGroup: adGroup.ref.itemKey,
        lineageKey: change.target.ref.lineageKey,
      });
      const common = {
        ref: { ...change.target.ref, itemKey },
        adGroupLineageKey: adGroup.ref.lineageKey,
        sourceTargetId: change.target.sourceTargetId,
        strategyKind: change.target.strategyKind,
        bid: {
          ...generatedValue(change.target.bid),
        },
        excluded: generatedValue(false),
        origin: "user_added" as const,
      };
      const target: SmartCreationReviewTarget =
        change.target.type === "keyword"
          ? {
              ...common,
              type: "keyword",
              keyword: change.target.keyword,
              matchType: {
                ...generatedValue(change.target.matchType),
              },
            }
          : {
              ...common,
              type: "product",
              asin: change.target.asin,
              ...(change.target.matchType === undefined
                ? {}
                : { matchType: change.target.matchType }),
            };
      graph.targets.push(target);
      adGroup.targetLineageKeys.push(target.ref.lineageKey);
      return;
    }
    case "remove_target": {
      const target = requireTarget(graph, change.ref);
      if (target.origin !== "user_added") {
        throw new SmartCreationReviewError(
          422,
          "smart_creation.review_target_invalid",
          "Generated Targets are excluded and restored rather than deleted",
        );
      }
      const adGroup = graph.adGroups.find(
        (candidate) => candidate.ref.lineageKey === target.adGroupLineageKey,
      );
      if (!adGroup || adGroup.targetLineageKeys.length === 1) {
        throw new SmartCreationReviewError(
          422,
          "smart_creation.review_target_invalid",
          "An Ad Group must retain at least one Target",
        );
      }
      workspace.graph.targets = graph.targets.filter(
        (candidate) => candidate.ref.lineageKey !== target.ref.lineageKey,
      );
      adGroup.targetLineageKeys = adGroup.targetLineageKeys.filter(
        (key) => key !== target.ref.lineageKey,
      );
      return;
    }
    case "add_negative": {
      const adGroup = requireAdGroup(graph, change.adGroupRef);
      if (
        change.negative.type === "keyword" &&
        !isSmartCreationNegativeKeywordWithinWordLimit(
          change.negative.text,
          change.negative.matchType,
        )
      ) {
        throw new SmartCreationReviewError(
          422,
          "smart_creation.review_negative_invalid",
          "Negative keyword exceeds its match type word limit",
        );
      }
      if (
        graph.negatives.some(
          (negative) => negative.ref.lineageKey === change.negative.ref.lineageKey,
        )
      ) {
        throw new SmartCreationReviewError(
          422,
          "smart_creation.review_negative_invalid",
          "Negative already exists",
        );
      }
      const negative: SmartCreationReviewNegative =
        change.negative.type === "keyword"
          ? {
              type: "keyword",
              ref: change.negative.ref,
              adGroupLineageKey: adGroup.ref.lineageKey,
              text: {
                ...generatedValue(change.negative.text),
              },
              matchType: {
                ...generatedValue(change.negative.matchType),
              },
              origin: "user",
            }
          : {
              type: "product",
              ref: change.negative.ref,
              adGroupLineageKey: adGroup.ref.lineageKey,
              asin: {
                ...generatedValue(change.negative.asin),
              },
              origin: "user",
            };
      graph.negatives.push(negative);
      adGroup.negativeLineageKeys.push(negative.ref.lineageKey);
      return;
    }
    case "update_negative": {
      const negative = requireNegative(graph, change.ref);
      if (negative.type === "keyword") {
        if (
          !isSmartCreationNegativeKeywordWithinWordLimit(
            change.patch.text ?? negative.text.effective,
            change.patch.matchType ?? negative.matchType.effective,
          )
        ) {
          throw new SmartCreationReviewError(
            422,
            "smart_creation.review_negative_invalid",
            "Negative keyword exceeds its match type word limit",
          );
        }
        if (change.patch.asin !== undefined) {
          throw new SmartCreationReviewError(
            422,
            "smart_creation.review_negative_invalid",
            "Keyword Negative cannot use ASIN",
          );
        }
        if (change.patch.text !== undefined)
          negative.text = userValue(negative.text, change.patch.text);
        if (change.patch.matchType !== undefined) {
          negative.matchType = userValue(negative.matchType, change.patch.matchType);
        }
      } else {
        if (change.patch.text !== undefined || change.patch.matchType !== undefined) {
          throw new SmartCreationReviewError(
            422,
            "smart_creation.review_negative_invalid",
            "Product Negative requires ASIN",
          );
        }
        if (change.patch.asin !== undefined)
          negative.asin = userValue(negative.asin, change.patch.asin);
      }
      return;
    }
    case "remove_negative": {
      const negative = requireNegative(graph, change.ref);
      workspace.graph.negatives = graph.negatives.filter(
        (candidate) => candidate.ref.lineageKey !== negative.ref.lineageKey,
      );
      for (const adGroup of graph.adGroups) {
        adGroup.negativeLineageKeys = adGroup.negativeLineageKeys.filter(
          (key) => key !== negative.ref.lineageKey,
        );
      }
      return;
    }
  }
}
